import { useEffect, useRef } from 'react';

import { isInGame } from '~/constants';
import { useAppDispatch, useAppSelector } from '~/store';
import { currentAccountActiveApplicationsCountSelector } from '~/store/selectors/currentAccountSelector';
import { isPoshtaReadySelector, activePoshtaMarkersByItemsSelector } from '~/store/selectors/poshtaSelectors';
import { poshtaActions } from '~/store/slices/poshtaSlice';
import { deactivatePoshtaMarkersThunk, getPoshtaMarkersThunk } from '~/store/thunks/poshtaThunks';

import { POSHTA_MARKER_NAME, POSHTA_MARKER_SOURCE } from '~/types/poshta';

export const usePoshtaInit = () => {
  const dispatch = useAppDispatch();
  const isPoshtaReady = useAppSelector(isPoshtaReadySelector);

  useEffect(() => {
    if (isPoshtaReady) {
      return;
    }

    void dispatch(getPoshtaMarkersThunk());
  }, [dispatch, isPoshtaReady]);
};

export const usePoshtaMarkersDeactivate = (markers: { name: POSHTA_MARKER_NAME; source: POSHTA_MARKER_SOURCE }[]) => {
  const dispatch = useAppDispatch();
  const activeMarkers = useAppSelector((state) => activePoshtaMarkersByItemsSelector(state, markers));

  useEffect(() => {
    if (activeMarkers.length === 0) {
      return;
    }

    void dispatch(deactivatePoshtaMarkersThunk(activeMarkers.map((m) => m.id)));
  }, [dispatch, activeMarkers]);
};

const applicationsPoshtaMarkers = [
  { source: POSHTA_MARKER_SOURCE.CLAN_REQUESTS, name: POSHTA_MARKER_NAME.NEW_APPLICATION },
];

export const usePoshtaMarkersClientSync = () => {
  const dispatch = useAppDispatch();

  const activeMarkers = useAppSelector((state) => activePoshtaMarkersByItemsSelector(state, applicationsPoshtaMarkers));
  const activeApplicationsCount = useAppSelector(currentAccountActiveApplicationsCountSelector);

  const activeApplicationsCountPrevRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isInGame) {
      return;
    }

    if (activeApplicationsCountPrevRef.current !== null) {
      if (activeApplicationsCountPrevRef.current > 0 && activeApplicationsCount === 0) {
        void dispatch(poshtaActions.deactivateMarkers(activeMarkers.map((m) => m.id)));
        void dispatch(poshtaActions.syncMarkersToClient());
      }
    }

    activeApplicationsCountPrevRef.current = activeApplicationsCount;
  }, [activeApplicationsCount, activeMarkers, dispatch]);
};
